import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { xor } from 'lodash-es';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { ChecklistActions } from './checklist.actions';
import { MY_TOPICS_ID } from '../../features/checklist/constants/my-topics-id';
import { ChecklistService } from '../../features/checklist/services/checklist.service';
import {
  WebSocketEntities,
  WebSocketReloadEvent,
} from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketWithWarningEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { ChecklistListSelectors } from '../checklist-list/checklist-list.selectors';
import { ExtractActions } from '../extract/extract.actions';
import { RootState } from '../root-state.model';
import { TopicListSelectors } from '../topic-list/topic-list.selectors';

@Injectable()
export class ChecklistEffects {
  private service = inject(ChecklistService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly webSocketService = inject(WebSocketService);

  private readonly notifications: PushNotificationRecord = {
    [ChecklistActions.SaveModelSuccess.type]: { message: 'Checklist saved', isSuccess: true },
    [ChecklistActions.SaveModelFail.type]: {
      message: 'Failed to save the checklist',
      isSuccess: false,
    },
    [ChecklistActions.LoadModelFail.type]: {
      message: 'Failed to load the checklist',
      isSuccess: false,
    },
  };

  toggleTopicEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActions.ToggleTopic),
      // Use the effective "My topics" id list instead of the raw extractOrder, so a user still on
      // the built-in default materializes the list they actually see (e.g. the asset's built-in
      // checklist topics) plus/minus the toggled topic. Resolve at the id level (myTopicIds) rather
      // than from the hydrated `myTopics.topicIds`: the hydrated list is `undefined` while built-in
      // checklists load (which would collapse the order to just the toggled id) and drops legacy
      // section ids that can't be hydrated against the DB topic list but are still part of the
      // configured checklist.
      withLatestFrom(this.store.select(TopicListSelectors.myTopicIds)),
      map(([action, myTopicIds]) =>
        ExtractActions.UpdateExtractOrder({
          order: xor(myTopicIds || [], [action.topicsId]),
        })
      )
    )
  );

  updateSelectedTopicsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActions.UpdateSelectedTopics),
      withLatestFrom(this.store, this.store.select(ChecklistListSelectors.selectedChecklist)),
      map(([action, store, selectedChecklist]) =>
        selectedChecklist.isMyTopics
          ? ExtractActions.UpdateExtractOrder({ order: action.topicsIds })
          : ChecklistActions.SaveModel({
              checklist: { ...selectedChecklist, topicIds: action.topicsIds } as any,
              loadExtract: true,
            })
      )
    )
  );

  loadModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActions.LoadModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        (action.id === MY_TOPICS_ID
          ? this.store.select(ChecklistListSelectors.myTopics).pipe(
              filter((myTopics) => !!myTopics.topicIds),
              take(1)
            )
          : this.service.getById(action.id)
        ).pipe(
          map((checklist) => ChecklistActions.LoadModelSuccess({ checklist })),
          reportEffectError(ChecklistActions.LoadModelFail)
        )
      )
    )
  );

  saveModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActions.SaveModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(
            action.checklist,
            true,
            !!action.checklist?.canShare,
            !!action.checklist?.canPublish
          )
          .pipe(
            map((checklist) =>
              ChecklistActions.SaveModelSuccess({ checklist, loadExtract: action.loadExtract })
            ),
            reportEffectError(ChecklistActions.SaveModelFail)
          )
      )
    )
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  wsEffect$: Observable<Action> = createWebSocketWithWarningEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Checklist,
    (state) => ChecklistActions.LoadModel({ id: state.checklist?.checklist?.id }),
    WebSocketReloadEvent.ChecklistReload
  );

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistActions.LoadModelFail),
      map(() => AppActions.Unauthorized())
    )
  );
}
